'use client';

import { AlertTriangle, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { BottomSheet } from '@/components/common/BottomSheet';
import { cn } from '@/lib/utils';

export interface ValidationItem {
  label: string;
  status: 'success' | 'warning' | 'error';
  message: string;
}

interface StatusChangeDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  description: string;
  impacts: string[];
  validations: ValidationItem[];
  warningLevel?: 'info' | 'warning' | 'danger';
  isLoading?: boolean;
}

const WarningLevelConfig = {
  info: {
    bgColor: 'bg-blue-50',
    iconColor: 'text-blue-500',
    borderColor: 'border-blue-200',
  },
  warning: {
    bgColor: 'bg-yellow-50',
    iconColor: 'text-yellow-500',
    borderColor: 'border-yellow-200',
  },
  danger: {
    bgColor: 'bg-red-50',
    iconColor: 'text-red-500',
    borderColor: 'border-red-200',
  },
};

const ValidationIcon = {
  success: CheckCircle,
  warning: AlertTriangle,
  error: XCircle,
};

const ValidationColors = {
  success: 'text-green-500',
  warning: 'text-yellow-500',
  error: 'text-red-500',
};

export function StatusChangeDialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  description,
  impacts,
  validations,
  warningLevel = 'warning',
  isLoading = false,
}: StatusChangeDialogProps) {
  const config = WarningLevelConfig[warningLevel];
  const hasErrors = validations.some((v) => v.status === 'error');

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={onClose}
      className="sm:max-w-lg"
      showCloseButton={false}
    >
      <div className="space-y-4">
        <div className="flex items-start">
          <div
            className={cn(
              'flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full',
              config.bgColor
            )}
          >
            <AlertTriangle
              className={cn('h-6 w-6', config.iconColor)}
              aria-hidden="true"
            />
          </div>
          <div className="ml-4">
            <h3 className="text-lg font-semibold leading-6 text-gray-900">
              {title}
            </h3>
            <div className="mt-2">
              <p className="text-sm text-gray-500">{description}</p>
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-gray-200 p-4">
          <h4 className="text-sm font-medium text-gray-900">
            This action will:
          </h4>
          <ul className="mt-2 list-inside list-disc space-y-1">
            {impacts.map((impact, index) => (
              <li key={index} className="text-sm text-gray-600">
                {impact}
              </li>
            ))}
          </ul>
        </div>

        <div className="space-y-4">
          <h4 className="text-sm font-medium text-gray-900">
            Pre-action Validation:
          </h4>
          {validations.map((validation, index) => {
            const Icon = ValidationIcon[validation.status];
            return (
              <div key={index} className="flex items-center space-x-2 text-sm">
                <Icon
                  className={cn('h-4 w-4', ValidationColors[validation.status])}
                />
                <span className="font-medium text-gray-900">
                  {validation.label}:
                </span>
                <span
                  className={cn(
                    'text-sm',
                    validation.status === 'error'
                      ? 'text-red-600'
                      : validation.status === 'warning'
                        ? 'text-yellow-600'
                        : 'text-green-600'
                  )}
                >
                  {validation.message}
                </span>
              </div>
            );
          })}
        </div>

        <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <Button
            variant="light"
            onClick={onClose}
            disabled={isLoading}
            className="w-full sm:w-auto"
          >
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            disabled={hasErrors || isLoading}
            isLoading={isLoading}
            className="w-full sm:w-auto"
          >
            Confirm
          </Button>
        </div>
      </div>
    </BottomSheet>
  );
}
